import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import chroma from 'chroma-js';

import type { MethodTypeKind } from '@protocols/use-cases';

/**
 * One checklist row of the multi-assign dialog: a method type that uses the
 * edited label, and the colour that pair carries today.
 *
 * Rows are merged from three sources by `LabelConfigMultiAssignEffects` — the
 * usage query, the session's staged configs, and the opener's context method
 * types — so `currentColor` may be persisted OR staged.
 */
export interface LabelUsageRow {
  methodTypeId: string;
  name: string;
  /** Kind of the method type's asset type; drives the row icon. */
  type?: MethodTypeKind;
  /** Id of the `LabelConfig` behind `currentColor`, carried so a submit upserts. */
  configId?: string;
  /** Colour of this (label, method type) pair today; `undefined` when unset. */
  currentColor?: string;
  /**
   * True for method types that do not exist in the database yet — created in the
   * opener's session. NOT true of a persisted method type whose association with
   * this label merely has not been staged: that is the ordinary case, and it is
   * why the usage read cannot return the row.
   */
  pending?: boolean;
}

/**
 * A method type the OPENER knows about but the backend usage query cannot return
 * — created or renamed in the current editing session. Plain values only: the
 * dialog never learns about the opener's staging states.
 *
 * Declared here rather than next to the effects that consume it: it is the
 * contract BETWEEN an opener and this dialog, so both sides import it from the
 * state tier instead of one session's effects importing the other's.
 */
export interface LabelConfigContextMethodType {
  id: string;
  name: string;
  type?: MethodTypeKind;
  /** True when the method type itself exists only client-side. */
  pending?: boolean;
}

/** A checklist row with the session's selection already applied to it. */
export interface LabelUsageChecklistRow extends LabelUsageRow {
  checked: boolean;
}

interface LabelConfigMultiAssignStateModel {
  /** The label being coloured; `null` before the session starts. */
  labelUuid: string | null;
  rows: LabelUsageRow[];
  loading: boolean;
  /** i18n key of the load failure, or `null`. */
  error: string | null;
  /** Method-type ids the picked colour will be applied to. */
  selectedIds: string[];
  /** Opaque colour as the picker emits it (alpha lives separately). */
  color: string | null;
  alpha: number;
}

const DEFAULT_ALPHA = 0.2;

const INITIAL_STATE: LabelConfigMultiAssignStateModel = {
  labelUuid: null,
  rows: [],
  loading: false,
  error: null,
  selectedIds: [],
  color: null,
  alpha: DEFAULT_ALPHA,
};

/**
 * Form + read state of ONE multi-assign dialog session. Provided on the dialog
 * host, so every open starts from `INITIAL_STATE` and dies with the dialog.
 *
 * Holds the whole form (selection, colour, alpha) on purpose: the dialog's
 * submit is a single derived decision over the checklist and the picker, so
 * splitting them across a reactive form and a store would make `canSubmit$`
 * span two sources of truth.
 */
@Injectable()
export class LabelConfigMultiAssignState extends ComponentStore<LabelConfigMultiAssignStateModel> {
  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly labelUuid$ = this.select((state) => state.labelUuid);
  public readonly rows$ = this.select((state) => state.rows);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly error$ = this.select((state) => state.error);
  public readonly selectedIds$ = this.select((state) => state.selectedIds);
  public readonly color$ = this.select((state) => state.color);
  public readonly alpha$ = this.select((state) => state.alpha);

  /** The rows the checklist renders, each carrying its own checked flag. */
  public readonly checklistRows$ = this.select((state) =>
    state.rows.map((row) => ({ ...row, checked: state.selectedIds.includes(row.methodTypeId) }))
  );

  /**
   * Interpolation params of the select-all header ("Apply to N of M method
   * types"). Derived here rather than combined in the template, which would
   * merge two streams in the view.
   */
  public readonly applyToCountParams$ = this.select((state) => ({
    checked: state.selectedIds.length,
    total: state.rows.length,
  }));

  public readonly allChecked$ = this.select(
    (state) => state.rows.length > 0 && state.selectedIds.length === state.rows.length
  );

  /** Partial selection — drives the header checkbox's indeterminate flag. */
  public readonly someChecked$ = this.select(
    (state) => state.selectedIds.length > 0 && state.selectedIds.length < state.rows.length
  );

  /**
   * The colour a submit would stage: the picked hue with the slider's alpha
   * folded in, as the 8-digit hex `LabelConfig` stores. `null` until picked.
   */
  public readonly pickedColor$ = this.select((state) =>
    state.color ? chroma(state.color).alpha(state.alpha).hex() : null
  );

  public readonly canSubmit$ = this.select((state) => state.selectedIds.length > 0 && state.color !== null);

  /**
   * Nothing to colour: the load settled and produced no row. Gated on `loading`
   * so the initial (still-empty) session renders the spinner, not the collapsed
   * empty state.
   */
  public readonly isEmpty$ = this.select((state) => !state.loading && state.rows.length === 0);

  // ============================================================================
  // Updaters
  // ============================================================================

  /** Opens a session for a label: clears any prior result and starts the load. */
  public readonly startSession = this.updater((state, labelUuid: string) => ({
    ...INITIAL_STATE,
    // Alpha and colour are session-scoped too, so the reset above is total.
    labelUuid,
    loading: true,
  }));

  public readonly setRows = this.updater((state, rows: LabelUsageRow[]) => {
    const ids = new Set(rows.map((row) => row.methodTypeId));
    return {
      ...state,
      rows,
      loading: false,
      error: null,
      selectedIds: state.selectedIds.filter((id) => ids.has(id)),
    };
  });

  /**
   * Points a freshly merged checklist at the method type the dialog was opened
   * FROM: checks its row alone, and loads the colour that pair carries today
   * into the picker (hue) and the transparency slider (alpha).
   *
   * Runs after `setRows` rather than inside it, on purpose: the seeded colour is
   * the MERGED one — a config staged earlier this session beats the persisted
   * value — and that only exists once the rows are published.
   */
  public readonly seedFromContext = this.updater((state, methodTypeId: string) => {
    const row = state.rows.find((candidate) => candidate.methodTypeId === methodTypeId);
    // Defensive. The merge materialises a row for every method type in the usage
    // read OR in the opener's context list, and an opener that has a method type
    // in context always contributes it to that list — so a caller honouring the
    // input contract always matches. An id from neither source seeds nothing.
    if (!row) return state;

    const seeded = { ...state, selectedIds: [methodTypeId] };
    // No colour on this pair yet: pre-check the row, but leave the picker and the
    // slider at their session defaults rather than guessing a colour.
    if (!row.currentColor) return seeded;

    // Stored colours are 8-digit hex and carry the alpha they were saved with, so
    // the slider seeds from the stored value — leaving it at the default would
    // turn a look-and-cancel into a silent transparency edit. A 6-digit colour has
    // no alpha component and therefore seeds fully opaque.
    const stored = chroma(row.currentColor);
    return { ...seeded, color: stored.alpha(1).hex(), alpha: stored.alpha() };
  });

  public readonly setError = this.updater((state, error: string | null) => ({ ...state, error, loading: false }));

  public readonly toggleRow = this.updater((state, methodTypeId: string) => ({
    ...state,
    selectedIds: state.selectedIds.includes(methodTypeId)
      ? state.selectedIds.filter((id) => id !== methodTypeId)
      : [...state.selectedIds, methodTypeId],
  }));

  /** Select-all header: a full selection clears, anything else selects all. */
  public readonly toggleAll = this.updater((state) => ({
    ...state,
    selectedIds: state.selectedIds.length === state.rows.length ? [] : state.rows.map((row) => row.methodTypeId),
  }));

  public readonly setColor = this.updater((state, color: string | null) => ({ ...state, color }));

  public readonly setAlpha = this.updater((state, alpha: number) => ({ ...state, alpha }));
}
